// Auth service - localStorage with DynamoDB sync
// New Cognito users are written to DynamoDB Users table on first sign-in

import { AVATARS } from '../data/mockData';
import { getUserByEmail, getUserByCognitoId, createUser as dbCreateUser, updateUser as dbUpdateUser, deleteUser, listUsers as dbListUsers, listMembershipsByUser, deleteMembership, createMembership } from './db';

const STORAGE_KEY = 'footballbets_auth';
// Sign-in can trigger several auth checks at once; share one sync per identity.
const syncsInFlight = new Map();

function getStoredUsers() {
  const stored = localStorage.getItem('footballbets_users');
  if (stored) return JSON.parse(stored);
  localStorage.setItem('footballbets_users', JSON.stringify([]));
  return [];
}

function saveUsers(users) {
  localStorage.setItem('footballbets_users', JSON.stringify(users));
}

export const authService = {
  /**
   * Fetch all users from DynamoDB and write them to localStorage.
   * Called once on login so all sync reads have fresh data.
   */
  async hydrateFromDb() {
    try {
      const dbUsers = await dbListUsers();
      if (Array.isArray(dbUsers)) {
        // Filter out invite placeholders, normalize shape
        const realUsers = dbUsers
          .filter((u) => !u.userId?.startsWith('invite:'))
          .map((u) => ({
            ...u,
            id: u.userId,
          }));
        // DynamoDB is authoritative; never retain browser-only demo accounts.
        saveUsers(realUsers);
        return realUsers;
      }
    } catch (err) {
      console.warn('[auth] hydrateFromDb failed:', err);
    }
    return getStoredUsers();
  },

  async signIn(email, password) {
    const users = getStoredUsers();
    const user = users.find((u) => u.email?.toLowerCase() === email.toLowerCase());
    if (!user) throw new Error('No account found with that email');
    if (user.enabled === false) throw new Error('Account is disabled');
    if (user.password && user.password !== password) throw new Error('Incorrect password');
    const session = { user, token: `mock-token-${user.id}-${Date.now()}` };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
  },

  async signOut() {
    localStorage.removeItem(STORAGE_KEY);
  },

  async getCurrentSession() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const session = JSON.parse(stored);
    const users = getStoredUsers();
    const freshUser = users.find((u) => u.id === session.user.id);
    if (!freshUser) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    session.user = freshUser;
    return session;
  },

  async getUser() {
    const session = await this.getCurrentSession();
    return session?.user || null;
  },

  async updateUserBalance(userId, newBalance) {
    const users = getStoredUsers();
    const idx = users.findIndex((u) => u.id === userId);
    if (idx === -1) return null; // silently skip deleted users
    users[idx].balance = newBalance;
    saveUsers(users);
    const session = await this.getCurrentSession();
    if (session && session.user.id === userId) {
      session.user.balance = newBalance;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
    return users[idx];
  },

  getAllUsers() {
    return getStoredUsers();
  },

  getEnabledUsers() {
    return getStoredUsers().filter((u) => u.enabled !== false);
  },

  addUser({ username, displayName, email, balance = 0 }) {
    const users = getStoredUsers();
    const existing = users.find(
      (u) => u.username.toLowerCase() === username.toLowerCase() || (email && u.email?.toLowerCase() === email.toLowerCase())
    );
    if (existing) throw new Error('Username or email already exists');

    const usedAvatars = new Set(users.map((u) => u.avatar));
    const avatar = AVATARS.find((a) => !usedAvatars.has(a)) || AVATARS[Math.floor(Math.random() * AVATARS.length)];

    const newUser = {
      id: `user${Date.now()}`,
      username: username.toLowerCase().trim(),
      email: email?.trim() || '',
      displayName: displayName.trim(),
      balance,
      totalWinnings: 0,
      avatar,
      role: 'player',
      enabled: true,
      location: null,
    };
    users.push(newUser);
    saveUsers(users);
    return newUser;
  },

  removeUser(userId) {
    const users = getStoredUsers();
    const idx = users.findIndex((u) => u.id === userId);
    if (idx === -1) throw new Error('User not found');
    if (users[idx].role === 'admin') throw new Error('Cannot remove the admin');
    users.splice(idx, 1);
    saveUsers(users);

    // Cascade: clean up memberships, bets, reactions
    const cleanKey = (key, filterFn) => {
      const stored = localStorage.getItem(key);
      if (stored) {
        const data = JSON.parse(stored);
        localStorage.setItem(key, JSON.stringify(data.filter(filterFn)));
      }
    };
    cleanKey('betbuds_memberships', (m) => m.userId !== userId);
    cleanKey('footballbets_bets', (b) => b.userId !== userId);
    cleanKey('footballbets_reactions', (r) => r.userId !== userId);
    cleanKey('betbuds_feed', (f) => f.userId !== userId);
    cleanKey('betbuds_activity_scores', (s) => s.userId !== userId);

    // Clean team memberships
    const teamsStr = localStorage.getItem('betbuds_teams');
    if (teamsStr) {
      const teams = JSON.parse(teamsStr);
      teams.forEach((t) => { t.members = (t.members || []).filter((id) => id !== userId); });
      localStorage.setItem('betbuds_teams', JSON.stringify(teams));
    }

    return true;
  },

  setEnabled(userId, enabled) {
    const users = getStoredUsers();
    const idx = users.findIndex((u) => u.id === userId);
    if (idx === -1) throw new Error('User not found');
    users[idx].enabled = enabled;
    saveUsers(users);
    return users[idx];
  },

  resetPassword(userId) {
    const users = getStoredUsers();
    const user = users.find((u) => u.id === userId);
    if (!user) throw new Error('User not found');
    return { success: true, message: `Password reset link sent to ${user.email || user.username}` };
  },

  inviteByEmail(email, displayName, startingBalance = 0) {
    const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    return this.addUser({ username, displayName: displayName || username, email, balance: startingBalance });
  },

  updateUser(userId, updates) {
    const users = getStoredUsers();
    const idx = users.findIndex((u) => u.id === userId);
    if (idx === -1) throw new Error('User not found');
    const allowed = ['displayName', 'email', 'avatar', 'avatarType', 'balance', 'nickname', 'location', 'enabled', 'cognitoId'];
    allowed.forEach((key) => {
      if (updates[key] !== undefined) users[idx][key] = updates[key];
    });
    saveUsers(users);
    return users[idx];
  },

  setLocation(userId, link) {
    return this.updateUser(userId, { location: { link, updatedAt: new Date().toISOString() } });
  },

  /**
   * Sync a Cognito user to DynamoDB on first sign-in.
   * Called from AuthContext after Cognito auth succeeds.
   * Falls back silently to localStorage-only if DynamoDB write fails.
   */
  syncCognitoUserToDb(input) {
    const key = input.cognitoId || input.email;
    if (!syncsInFlight.has(key)) {
      syncsInFlight.set(key, this._syncCognitoUserToDb(input).finally(() => syncsInFlight.delete(key)));
    }
    return syncsInFlight.get(key);
  },

  async _syncCognitoUserToDb({ email, displayName, cognitoId, avatar, avatarType }) {
    try {
      // Check if user already exists in DynamoDB
      let dbUser = await getUserByCognitoId(cognitoId);
      if (dbUser) {
        console.log('[auth] User already in DynamoDB:', dbUser.userId);
        return dbUser;
      }

      dbUser = await getUserByEmail(email);
      if (dbUser) {
        if (dbUser.userId?.startsWith('invite:')) {
          // This is a placeholder from an invite — create a proper user record
          console.log('[auth] Found invite placeholder, creating proper user for:', email);
          const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
          const properUser = await dbCreateUser({
            userId: cognitoId || `user${Date.now()}`,
            email: email.toLowerCase(),
            username,
            displayName: dbUser.displayName || displayName || username,
            balance: dbUser.balance || 0,
            totalWinnings: dbUser.totalWinnings || 0,
            avatar: avatar || dbUser.avatar || null,
            avatarType: avatarType || dbUser.avatarType || null,
            role: dbUser.role || 'player',
            enabled: true,
            cognitoId,
            nickname: dbUser.nickname || null,
          });
          // Delete the placeholder
          try { await deleteUser(dbUser.userId); } catch (_) { /* ignore */ }
          console.log('[auth] Created proper user:', properUser?.userId, 'deleted placeholder:', dbUser.userId);

          await this._resolvePendingMemberships(email, cognitoId);
          return properUser;
        }

        // Real user exists by email but missing cognitoId — link them
        console.log('[auth] Linking cognitoId to existing DynamoDB user:', dbUser.userId);
        const updated = await dbUpdateUser(dbUser.userId, { cognitoId });

        // Resolve any pending invite memberships for this email
        await this._resolvePendingMemberships(email, cognitoId);

        return updated || dbUser;
      }

      // New user — keyed by the Cognito sub so a concurrent second create collides
      // with the first instead of adding a duplicate row.
      const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
      const newUser = await dbCreateUser({
        userId: cognitoId || `user${Date.now()}`,
        email: email.toLowerCase(),
        username,
        displayName: displayName || username,
        balance: 0,
        totalWinnings: 0,
        avatar: avatar || null,
        avatarType: avatarType || null,
        role: 'player',
        enabled: true,
        cognitoId,
        nickname: null,
      });
      console.log('[auth] Created DynamoDB user:', newUser?.userId);

      // Resolve any pending invite memberships for this email
      await this._resolvePendingMemberships(email, cognitoId);

      return newUser;
    } catch (err) {
      console.warn('[auth] DynamoDB sync failed, localStorage still active:', err);
      return null;
    }
  },

  /**
   * Migrate pending invite memberships from invite:email to the real Cognito userId.
   * Membership keys include userId, so we delete the old record and create a new one.
   */
  async _resolvePendingMemberships(email, cognitoId) {
    const pendingUserId = `invite:${email.trim().toLowerCase()}`;
    try {
      const pendingMemberships = await listMembershipsByUser(pendingUserId);
      if (!pendingMemberships || pendingMemberships.length === 0) return;

      console.log(`[auth] Resolving ${pendingMemberships.length} pending membership(s) for ${pendingUserId} -> ${cognitoId}`);
      for (const mem of pendingMemberships) {
        // Create new membership with real Cognito userId
        await createMembership({
          masterEventId: mem.masterEventId,
          userId: cognitoId,
          role: mem.role,
          status: 'active',
          joinedAt: mem.joinedAt,
        });
        // Delete the old pending one
        await deleteMembership(mem.masterEventId, pendingUserId);
        console.log(`[auth] Resolved membership: ${mem.masterEventId} ${pendingUserId} -> ${cognitoId}`);
      }
    } catch (err) {
      console.warn('[auth] Failed to resolve pending memberships:', err);
    }
  },
};
